import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import React, { CSSProperties, useEffect, useState } from "react";
import { createPortal } from "react-dom";
//Added due to error: Property 'fonts' does not exist on type 'Document'.
//https://github.com/Microsoft/TypeScript/issues/30984
import type {} from "css-font-loading-module";
import { Box } from "@mui/material";

declare const InstallTrigger: any;
const IFRAME_PADDING = 30;

type ResponsiveIframeProps = {
  title: string;
  srcDoc: string;
  containerStyles?: CSSProperties;
  forceTableBorders?: boolean;
  fullHeight?: boolean;
};

const useStyles = makeStyles({
  container: {
    position: "relative",
    overflow: "hidden",
    width: "100%",
  },
});

const ResponsiveIframe = ({
  title,
  srcDoc,
  containerStyles,
  forceTableBorders,
  fullHeight,
  ...rest
}: ResponsiveIframeProps &
  React.DetailedHTMLProps<
    React.IframeHTMLAttributes<HTMLIFrameElement>,
    HTMLIFrameElement
  >) => {
  const styles = useStyles(useTheme());
  const [contentRef, setContentRef] = useState<HTMLIFrameElement | null>(null);
  const [innerDivRef, setInnerDivRef] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(IFRAME_PADDING);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, triggerFakeFirefoxRender] = useState(0);

  useEffect(() => {
    if (!innerDivRef) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setHeight(innerDivRef.clientHeight + IFRAME_PADDING);
    });

    observer.observe(innerDivRef);

    return () => {
      observer.disconnect();
    };
  }, [innerDivRef]);

  const mountNode = contentRef?.contentWindow?.document?.body;
  const currentFonts = window.document.fonts;
  const currentFontFamily = getComputedStyle(
    window.document.body
  ).getPropertyValue("font-family");

  // Temporary workaround for firefox issue when no answers displayed
  // setTimeout is used to move render call into macrotasks queue
  // allowing the render to happen apart from page loading process
  const isFirefox = typeof InstallTrigger !== "undefined";
  if (isFirefox) {
    setTimeout(() => triggerFakeFirefoxRender((value) => value + 1));
  }

  if (contentRef?.contentDocument) {
    contentRef.contentDocument.fonts.clear();
    currentFonts.forEach((font) => {
      try {
        contentRef?.contentDocument?.fonts.add(font);
      } catch (error: any) {
        // Fix for safari issue: this exception is being thrown
        // when we try to add font which is already included via the CSS @font-face rule.
        // Whole <ResponsiveIframe> needs to be refactored to avoid any further issues.
        if (error.code !== DOMException.INVALID_MODIFICATION_ERR) {
          throw error;
        }
      }
    });
    contentRef.contentDocument.body.style.fontFamily = currentFontFamily;
    contentRef.contentDocument.body.style.margin = "0";
  }

  function applyTableBorders(html: string) {
    if (!html) {
      return html;
    }

    const htmlDoc = new DOMParser().parseFromString(html, "text/html");
    const tables = htmlDoc.getElementsByTagName("table");

    for (var i = 0; i < tables.length; i++) {
      const currentBorder = tables[i].getAttribute("border");

      if (!currentBorder || currentBorder === "0") {
        tables[i].setAttribute("border", "1");
      }
    }

    return new XMLSerializer().serializeToString(htmlDoc);
  }

  function renderHtml() {
    return (
      <div
        ref={setInnerDivRef}
        dangerouslySetInnerHTML={{
          __html: forceTableBorders ? applyTableBorders(srcDoc) : srcDoc,
        }}
      />
    );
  }

  return (
    <Box
      style={containerStyles}
      sx={styles.container}
      height={fullHeight ? "100%" : height}
    >
      <iframe
        ref={setContentRef}
        title={title}
        style={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: "100%",
          height: fullHeight ? "100%" : height,
          border: 0,
        }}
        {...rest}
      >
        {mountNode && createPortal(renderHtml(), mountNode)}
      </iframe>
    </Box>
  );
};

export default ResponsiveIframe;
